// Implements: F-UIX-020, F-UIX-008, F-UIX-009, F-UIX-010
/**
 * One profile in the editor: its stored values, the person's draft and the save.
 *
 * The parent mounts it with `key` = the profile id (checklist 14), so the draft
 * of profile A can never survive into profile B: a new id is a new container
 * with a fresh draft. The draft starts as the latest stored version, or, for a
 * new profile, as the system default the server serves (never a client copy),
 * and is only replaced by what the person types; a refused save keeps it
 * (F-UIX-008).
 *
 * A stored profile also carries its logo and reference images
 * (`ReferenceUploaderContainer`, F-UIX-020) and the "Activo en" switches of the scopes the person
 * manages (`ActiveInContainer`, mockup v3 P5); a new one gets them once it exists.
 *
 * A refusal is a message next to its field. The server's contrast refusal names
 * its pairs (and only its pairs, never a colour), so a message that names a
 * declared pair goes next to the palette and any other one under the form.
 *
 * Audit P5 (mockup v3 "Ver perfil"): a profile the server does not let the person edit opens in the
 * same editor, read-only (`mode="view"`), with its preview and contrast, a notice that says why, and
 * "Duplicar y editar" when the person may create somewhere.
 *
 * Owner 2026-10-19 (answers 4A and 2A): "Duplicar y editar" is always the server's instant copy
 * (`ProfileDuplicateContainer`, logo and reference images included), on an editable profile and on a
 * read-only one alike; the server puts the copy of a read-only profile in the person's personal
 * scope. The parent opens the copy (`onDuplicated`), which announces itself (`duplicated`).
 */

import { useState } from "react";
import type { ReactElement } from "react";

import type { ApiError } from "../../../shared/api/ApiError";
import { useSession } from "../../../shared/api/session";
import { useTranslation } from "../../../shared/i18n";
import { ErrorState } from "../../../shared/ui/molecules/ErrorState";
import { LoadingState } from "../../../shared/ui/molecules/LoadingState";
import { userMessage } from "../../../shared/ui/molecules/userMessage";
import type { ProfileVersionValues, ScopeKeyView } from "../../../types/design";
import { assetPath } from "../api/designApi";
import { ProfileEditor } from "../components/ProfileEditor";
import { ProfileReadOnly } from "../components/ProfileReadOnly";
import { pairLabel } from "../components/contrastPairs";
import { useProfile, useSaveProfile } from "../hooks/useDesignProfiles";
import { useGoogleFont } from "../hooks/useGoogleFont";
import type { SaveRequest } from "../hooks/useDesignProfiles";
import { googleFontLinks, isEditable, pairsNamedIn, valuesOf } from "../model/profileModel";
import { ActiveInContainer } from "./ActiveInContainer";
import type { ActiveInScope } from "./ActiveInContainer";
import { ProfileDeleteContainer } from "./ProfileDeleteContainer";
import { ProfileDuplicateContainer } from "./ProfileDuplicateContainer";
import { ProfileNameContainer } from "./ProfileNameContainer";
import { ReferenceUploaderContainer } from "./ReferenceUploaderContainer";

/** A scope the server lets the person create in (`may_edit`), as the create form offers it. */
export interface CreatableScope {
  /** `scopeKey`: the value of its option. */
  readonly value: string;
  readonly label: string;
  readonly scope: ScopeKeyView;
}

/** The refusals of the profile routes whose sentence the server writes for the person. */
const SAVE_REFUSAL_CODES = ["CONFLICT"] as const;

export interface ProfileEditorContainerProps {
  /** `null` opens the editor on a new profile. */
  readonly profileId: string | null;
  /** The profile a new one starts from: the system default the server serves. */
  readonly seedProfileId: string;
  /** Where a new profile may be created, the first being the default (W2l); unused when editing. */
  readonly creatableScopes: readonly CreatableScope[];
  /** A version the screen already stored for this profile (its creation), to announce. */
  readonly announcedVersion: number | null;
  /**
   * A save stored a profile. `requestedFor` is the id this container was opened
   * on (`null` for a new one), so the parent can ignore an answer by id.
   */
  readonly onSaved: (requestedFor: string | null, savedId: string, version: number) => void;
  /** Every scope of the listing as "Activo en" shows it for the open profile (mockup v3 P5). */
  readonly activeScopes?: readonly ActiveInScope[] | undefined;
  /** The open profile was deleted (owner 2026-10-19), named by id so the parent can ignore it. */
  readonly onDeleted?: ((profileId: string) => void) | undefined;
  /** A read-only profile was opened by the person (a list row): its notice takes the focus. */
  readonly focusNotice?: boolean | undefined;
  /** "Duplicar y editar": the server stored the copy `copyId` of `sourceId`. */
  readonly onDuplicated?: ((sourceId: string, copyId: string) => void) | undefined;
  /** The profile is a copy just made: the editor says so once. */
  readonly duplicated?: boolean | undefined;
}

export function ProfileEditorContainer({
  profileId,
  seedProfileId,
  creatableScopes,
  announcedVersion,
  onSaved,
  activeScopes,
  onDeleted,
  focusNotice = false,
  onDuplicated,
  duplicated = false,
}: ProfileEditorContainerProps): ReactElement {
  const { t } = useTranslation();
  const { state: session } = useSession();
  // The profile itself, or the system default a new profile starts from.
  const source = useProfile(profileId ?? seedProfileId);
  const save = useSaveProfile();
  const [draft, setDraft] = useState<ProfileVersionValues | null>(null);
  const [name, setName] = useState("");
  const [scopeChoice, setScopeChoice] = useState<string | null>(null);
  const [pending, setPending] = useState(false);
  const [refusal, setRefusal] = useState<ApiError | null>(null);
  const [savedVersion, setSavedVersion] = useState<number | null>(announcedVersion);
  // Only the families this editor shows are loaded (titles and text), never the whole catalogue.
  const shown = draft ?? (source.data === undefined ? undefined : valuesOf(source.data.version));
  useGoogleFont(googleFontLinks(shown === undefined ? [] : [shown.heading_font, shown.body_font]));

  if (source.isError) {
    // The generic state says that something failed; the sentence above it says what.
    return (
      <div>
        <p>{t("design.editor.loadFailed")}</p>
        <ErrorState
          error={source.error}
          onRetry={() => {
            void source.refetch();
          }}
        />
      </div>
    );
  }
  if (source.data === undefined || session.status !== "authenticated") {
    return <LoadingState label={t("common.state.loading")} />;
  }
  const stored = profileId === null ? undefined : source.data;
  // The server decides who edits: a profile it marks read-only (or the system default) never
  // reaches the editor or the uploader, however it was opened (a row or a `?profile=` link).
  if (stored !== undefined && !isEditable(stored)) {
    // The system default is named as the list names it, in the person's words.
    const shownName = stored.is_system_default ? t("design.list.systemDefault") : stored.name;
    return (
      <ProfileReadOnly
        system={stored.is_system_default}
        name={shownName}
        currentVersion={stored.latest_version}
        savedBy={{ at: stored.version.created_at, name: stored.version.created_by_name }}
        values={valuesOf(stored.version)}
        images={stored.version.images.map((image) => ({
          key: image.key,
          src: assetPath(stored.id, stored.version.version, image.asset_id),
          alt: image.alt_text,
        }))}
        focusNotice={focusNotice}
        duplicateAction={
          onDuplicated === undefined || creatableScopes.length === 0 ? undefined : (
            <ProfileDuplicateContainer
              key={`duplicate:${stored.id}`}
              profileId={stored.id}
              onDuplicated={onDuplicated}
              primary
            />
          )
        }
      />
    );
  }
  const values = draft ?? valuesOf(source.data.version);
  // The person's pick while the server still offers it; else the first scope offered.
  const target = creatableScopes.find((item) => item.value === scopeChoice) ?? creatableScopes[0];

  const submit = async (): Promise<void> => {
    let request: SaveRequest;
    if (profileId !== null) request = { kind: "version", profileId, values };
    else if (target !== undefined) request = { kind: "create", scope: target.scope, name: name.trim(), values };
    // No scope offered: the screen offers no creation then, and nothing is sent.
    else return;
    setPending(true);
    setRefusal(null);
    setSavedVersion(null);
    const outcome = await save(request);
    setPending(false);
    if (!outcome.ok) {
      setRefusal(outcome.error);
      return;
    }
    const saved = outcome.detail;
    setSavedVersion(saved?.version.version ?? null);
    if (saved !== undefined) onSaved(profileId, saved.id, saved.version.version);
  };

  const message =
    refusal === null ? undefined : userMessage(refusal, t("design.editor.failed"), SAVE_REFUSAL_CODES);
  const named = message === undefined ? [] : pairsNamedIn(message);
  // The server names pairs by slug; the person reads them in the words of the live list.
  const paletteError =
    named.length === 0
      ? undefined
      : `${t("design.contrast.refused")} ${named.map((pair) => t(pairLabel(pair))).join(", ")}.`;

  const logo = stored?.version.images.find((image) => image.key === "logo");
  const editor = (parts?: { readonly logo: ReactElement; readonly references: ReactElement; readonly count: number }): ReactElement => (
    <ProfileEditor
      mode={profileId === null ? "create" : "edit"}
      scopeOptions={creatableScopes}
      scope={target?.value ?? ""}
      onScopeChange={setScopeChoice}
      name={stored === undefined ? name : stored.name}
      onNameChange={setName}
      currentVersion={stored?.latest_version ?? null}
      savedBy={stored === undefined ? undefined : { at: stored.version.created_at, name: stored.version.created_by_name }}
      values={values}
      onValuesChange={setDraft}
      onSubmit={() => {
        void submit();
      }}
      pending={pending}
      paletteError={paletteError}
      formError={paletteError === undefined ? message : undefined}
      savedVersion={savedVersion}
      announcement={duplicated ? t("design.duplicate.done") : undefined}
      logoSrc={stored === undefined || logo === undefined ? undefined : assetPath(stored.id, stored.version.version, logo.asset_id)}
      logo={parts?.logo}
      references={parts?.references}
      referenceCount={parts?.count}
      nameField={stored === undefined ? undefined : <ProfileNameContainer key={stored.id} profileId={stored.id} name={stored.name} />}
      duplicateAction={
        stored === undefined || onDuplicated === undefined ? undefined : (
          <ProfileDuplicateContainer key={`duplicate:${stored.id}`} profileId={stored.id} onDuplicated={onDuplicated} />
        )
      }
      deleteAction={
        stored === undefined ? undefined : (
          <ProfileDeleteContainer key={stored.id} profileId={stored.id} onDeleted={onDeleted} />
        )
      }
      activeIn={
        stored === undefined || activeScopes === undefined ? undefined : (
          <ActiveInContainer scopes={activeScopes} profileId={stored.id} />
        )
      }
    />
  );
  // Images attach to a stored profile only: each upload saves its next version.
  if (stored === undefined) return editor();
  return (
    <ReferenceUploaderContainer
      profileId={stored.id}
      profileName={stored.name}
      version={stored.version.version}
      images={stored.version.images}
    >
      {editor}
    </ReferenceUploaderContainer>
  );
}

